import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import { Mail, Lock, User, AlertCircle, Eye, EyeOff, MapPin, Phone } from "lucide-react";
import Logo from "@/components/brand/Logo";
import FixyBeeMark from "@/components/brand/FixyBeeMark";
import CategoryPicker from "@/components/categories/CategoryPicker";
import { Button } from "@/ds";
import {
  FieldError, GoogleMark, PasswordRules, emailOk, passwordOk, phoneForServer,
  phoneOk, zipOk,
} from "@/components/auth/SignupParts";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { clearAcquisition, getAcquisition } from "@/lib/acquisition";
import { errorCode, errorMessage } from "@/lib/errors";
import { INVITE_PROVIDER_ONBOARDING, armPushInvite } from "@/lib/push";
import { readReturnTo, withReturnTo } from "@/lib/returnTo";

/**
 * `/signup/client` and `/signup/provider` (2026-10-28).
 *
 * Two doors, and the door IS the role: there is no "client or FixyBee?" screen
 * any more, and no account is created without one — the server refuses a body
 * without `role`. One component because the two forms share every field they
 * have in common; the page decides which fields exist and how many steps.
 *
 *   * Client, one step: name, email, password (or Google), ZIP.
 *   * FixyBee, two steps: the same plus a phone — captured, NOT verified; the
 *     code is asked for from the dashboard and checked at the first quote —
 *     then one main trade.
 *
 * **The account is created at the end, in one call.** For Google that means
 * the access token from step 1 is held until the last press: an abandoned
 * step 2 leaves no half-made FixyBee behind, and the server gets the role and
 * the profile together. The first Google call goes out WITHOUT a role, as a
 * probe: an existing account is simply signed in, and a new one answers
 * `role_required` and creates nothing, so the form carries on.
 *
 * Signed in with no role — an account from before this redesign — the page
 * finishes that account instead: no email or password, the same profile
 * fields, through `POST /users/me/onboarding`.
 *
 * **An existing account never meets "already exists" (T25).** On the FixyBee
 * door, a client — signed in already, or signed in here by Google or by the
 * email and password just typed — converts the same account: the page asks
 * only for the phone, ZIP and trade, prefilled with what it has, and sends
 * them to `POST /users/me/become-provider`. On the client door, an address
 * that is taken is tried as a login with the password typed, and lands on
 * the panel. Only a wrong password still reads "log in with it": nobody can
 * be signed in to an account without its credential.
 */
export default function Signup({ role }) {
  const isProvider = role === "provider";
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading, signupEmail, loginEmail, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = readReturnTo(searchParams);
  const language = String(i18n.language || "").toLowerCase().startsWith("es") ? "es" : "en";

  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [categories, setCategories] = useState([]);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  // { token, email } once Google said yes and no account exists for it yet.
  const [google, setGoogle] = useState(null);
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [emailTaken, setEmailTaken] = useState(false);
  const [loading, setLoading] = useState(false);
  // Set while this page is creating (or signing in) the account. `setUser`
  // re-renders it with a role before `done()` navigates, and without this the
  // "already has a role" redirect below wins the race and a brand-new client
  // lands on the dashboard instead of the job form — or past its `returnTo`.
  const leaving = useRef(false);

  const finishing = !!user && !user.profile?.role;
  // T25: a client on the FixyBee door. Same account, the FixyBee half only.
  const converting = isProvider && user?.profile?.role === "client";
  const usesPassword = !finishing && !converting && !google;

  // What the account already holds is not asked for twice. Once per account,
  // and never over what the person has typed meanwhile.
  const prefilled = useRef(null);
  useEffect(() => {
    if (!converting || prefilled.current === user.user_id) return;
    prefilled.current = user.user_id;
    const p = user.profile || {};
    if (p.phone) setPhone((v) => v || p.phone);
    if (p.location?.zip) setZip((v) => v || String(p.location.zip).slice(0, 5));
  }, [converting, user]);

  const touch = (key) => setTouched((s) => ({ ...s, [key]: true }));
  const problems = {
    name: !converting && fullName.trim().length < 2,
    email: usesPassword && !emailOk(email),
    password: usesPassword && !passwordOk(password),
    zip: !zipOk(zip),
    phone: isProvider && !phoneOk(phone),
    legal: usesPassword && !acceptedLegal,
  };
  const stepOneOk = !Object.values(problems).some(Boolean);
  const show = (key) => touched[key] && problems[key];

  const landing = () =>
    returnTo || (isProvider ? "/provider/dashboard" : "/client/jobs/new");

  const profilePayload = () => ({
    zip: zip.trim(),
    ...(isProvider ? { phone: phoneForServer(phone), service_categories: categories } : {}),
  });

  const done = () => {
    // The FixyBee is about to start waiting for jobs in their area, which is
    // the sentence push is for. Armed rather than rendered: this page leaves.
    if (isProvider) armPushInvite(INVITE_PROVIDER_ONBOARDING);
    navigate(landing(), { replace: true });
  };

  const fail = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail === "Email already registered") {
      setEmailTaken(true);
      setStep(1);
      return;
    }
    const code = errorCode(err);
    const field = { zip_required: "zip", phone_invalid: "phone", category_required: "category" }[code];
    if (field === "category") {
      setError(t("signupX.categoryRequired"));
      return;
    }
    if (field) {
      setTouched((s) => ({ ...s, [field]: true }));
      setStep(1);
    }
    setError(field ? "" : errorMessage(err, t("common.error")));
  };

  const create = async () => {
    // A second Enter while the first call is in flight (the button is busy,
    // not disabled, so the form can still submit).
    if (leaving.current) return;
    setError("");
    setEmailTaken(false);
    setLoading(true);
    leaving.current = true;
    try {
      if (converting) {
        await api.post("/users/me/become-provider", profilePayload());
        await refresh();
      } else if (finishing) {
        await api.post("/users/me/onboarding", {
          role, full_name: fullName.trim() || user.profile?.full_name || "", ...profilePayload(),
        });
        await refresh();
      } else if (google) {
        const acquisition = getAcquisition();
        const { data } = await api.post("/auth/google", {
          access_token: google.token, role, language, full_name: fullName.trim(),
          ...(acquisition ? { acquisition } : {}), ...profilePayload(),
        });
        clearAcquisition();
        setUser(data.user);
      } else {
        try {
          await signupEmail(email.trim(), password, fullName.trim(), { role, ...profilePayload() });
        } catch (err) {
          // T25: the address has an account. The client door signs it in with
          // the password just typed; the FixyBee door already tried that at
          // step 1, so here it can only be a wrong password.
          const taken = err?.response?.data?.detail === "Email already registered";
          if (!taken || isProvider) throw err;
          const data = await loginEmail(email.trim(), password).catch(() => { throw err; });
          navigate(returnTo || dashboardOf(data.user), { replace: true });
          return;
        }
      }
      done();
    } catch (err) {
      leaving.current = false;
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError("");
      setLoading(true);
      try {
        // The probe: no role, so it can only sign in an account that exists.
        const { data } = await api.post("/auth/google", {
          access_token: tokenResponse.access_token, language,
        });
        setUser(data.user);
        const had = data.user?.profile?.role;
        // A client on the FixyBee door stays: this page now converts them.
        if (had && !(isProvider && had === "client")) {
          navigate(returnTo || dashboardOf(data.user), { replace: true });
        }
        // Role-less: the account exists and this page now finishes it.
      } catch (err) {
        if (errorCode(err) !== "role_required") {
          setError(errorMessage(err, t("auth.googleFail")));
          return;
        }
        const who = await googleProfile(tokenResponse.access_token);
        setGoogle({ token: tokenResponse.access_token, email: who.email || "" });
        if (!fullName.trim() && who.name) setFullName(who.name);
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError(t("auth.googleFail")),
  });

  // After every hook: the Google one above must run on every render.
  if (authLoading) return null;
  if (user && user.profile?.role && !converting && !leaving.current) {
    // Already on a side of the marketplace: this page has nothing to create.
    return <Navigate to={returnTo || dashboardOf(user)} replace />;
  }

  const submitStepOne = async (e) => {
    e.preventDefault();
    if (!stepOneOk) {
      setTouched({ name: true, email: true, password: true, zip: true, phone: true, legal: true });
      return;
    }
    if (!isProvider) {
      create();
      return;
    }
    setError("");
    if (usesPassword) {
      // T25: an address that already has an account, with its password, is
      // signed in here and converted rather than told it exists. A 401 is the
      // ordinary new FixyBee and costs nothing: the login creates nothing.
      setLoading(true);
      try {
        const data = await loginEmail(email.trim(), password);
        // A client re-renders this page in convert mode, with the phone and
        // ZIP just typed; a FixyBee or admin is sent home by the guard above.
        if (data.user?.profile?.role === "client") return;
      } catch (err) {
        if (err?.response?.status === 403) {
          setError(errorMessage(err, t("common.error")));
          return;
        }
      } finally {
        setLoading(false);
      }
    }
    setStep(2);
    window.scrollTo(0, 0);
  };

  const otherSide = isProvider ? "/signup/client" : "/signup/provider";
  const title = converting ? t("signupX.convertTitle")
    : finishing ? t("signupX.finishTitle")
    : isProvider ? t("signupX.providerTitle") : t("signupX.clientTitle");
  const subtitle = finishing ? t("signupX.finishSubtitle")
    : isProvider ? t("signupX.providerSubtitle") : t("signupX.clientSubtitle");

  return (
    <div className="min-h-screen flex flex-col md:flex-row-reverse bg-cream"
         data-testid={`signup-${role}-page`}>
      <div className="hidden md:flex md:w-1/2 bg-worker text-cream relative items-center justify-center p-12 overflow-hidden">
        <div className="absolute top-8 right-8 bg-cream rounded-md px-3 py-2">
          <Logo size={32} />
        </div>
        <div className="relative max-w-md flex flex-col items-center">
          <div className="animate-bee-wings"><FixyBeeMark size={180} /></div>
          <h2 className="mt-8 font-display text-3xl font-bold text-center text-balance">{title}</h2>
          <p className="mt-3 text-cream/70 text-center max-w-sm">{subtitle}</p>
        </div>
      </div>

      <div className="flex-1 flex items-start md:items-center justify-center px-4 py-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-6 flex items-center justify-between">
            <Logo size={36} />
            {isProvider && (
              <span className="font-mono text-xs text-smoke" data-testid="signup-step">
                {t("onboarding.stepOf", { n: step, total: 2 })}
              </span>
            )}
          </div>
          {isProvider && (
            <p className="hidden md:block font-mono text-xs text-smoke mb-2" data-testid="signup-step-desktop">
              {t("onboarding.stepOf", { n: step, total: 2 })}
            </p>
          )}

          {error && (
            <div className="mb-4 p-4 rounded-sm bg-danger/10 border border-danger/30 text-danger flex gap-2 items-start text-sm" role="alert" data-testid="signup-error">
              <AlertCircle size={18} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {step === 1 && (
            <>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-worker text-balance">{title}</h1>
              <p className="mt-2 text-smoke">{subtitle}</p>

              {!finishing && !converting && !google && (
                <>
                  <Button
                    type="button" variant="secondary" fullWidth leading={<GoogleMark />}
                    onClick={() => handleGoogle()} disabled={loading} className="mt-6"
                    data-testid="signup-google-btn"
                  >
                    {t("auth.continueGoogle")}
                  </Button>
                  <p className="mt-2 text-xs text-smoke text-center" data-testid="signup-google-consent">
                    <Trans
                      i18nKey="auth.googleConsent"
                      components={{
                        terms: <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-worker" />,
                        privacy: <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-worker" />,
                      }}
                    />
                  </p>
                  <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-smoke">
                    <div className="h-px flex-1 bg-sand"></div> {t("auth.or")} <div className="h-px flex-1 bg-sand"></div>
                  </div>
                </>
              )}

              {google && (
                <div className="mt-6 mb-2 p-3 rounded-sm bg-linen flex items-center justify-between gap-3 text-sm" data-testid="signup-google-chosen">
                  <span className="flex items-center gap-2 min-w-0">
                    <GoogleMark />
                    <span className="truncate">{t("signupX.googleChosen", { email: google.email })}</span>
                  </span>
                  <button type="button" onClick={() => setGoogle(null)} className="font-semibold text-worker underline shrink-0 min-h-[44px]" data-testid="signup-google-change">
                    {t("signupX.googleChange")}
                  </button>
                </div>
              )}

              <form onSubmit={submitStepOne} noValidate className={`space-y-4 ${finishing || converting ? "mt-6" : ""}`} data-testid="signup-form">
                {!converting && <div>
                  <label htmlFor="su-name" className="label-base">{t("auth.name")}</label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
                    <input
                      id="su-name" value={fullName} autoComplete="name"
                      onChange={(e) => setFullName(e.target.value)} onBlur={() => touch("name")}
                      className="input-base pl-10 min-h-[48px]" placeholder={t("auth.namePlaceholder")}
                      aria-invalid={!!show("name")} data-testid="signup-name"
                    />
                  </div>
                  <FieldError testid="signup-name-error">{show("name") && t("signupX.nameInvalid")}</FieldError>
                </div>}

                {usesPassword && (
                  <>
                    <div>
                      <label htmlFor="su-email" className="label-base">{t("auth.email")}</label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
                        <input
                          id="su-email" type="email" inputMode="email" autoComplete="email" value={email}
                          onChange={(e) => { setEmail(e.target.value); setEmailTaken(false); }}
                          onBlur={() => touch("email")}
                          className="input-base pl-10 min-h-[48px]" placeholder={t("auth.emailPlaceholder")}
                          aria-invalid={!!(show("email") || emailTaken)} data-testid="signup-email"
                        />
                      </div>
                      <FieldError testid="signup-email-error">
                        {emailTaken ? (
                          <>
                            {t("signupX.emailTaken")}{" "}
                            <Link to={withReturnTo("/login", returnTo)} className="font-semibold underline">{t("auth.loginBtn")}</Link>
                          </>
                        ) : show("email") && t("signupX.emailInvalid")}
                      </FieldError>
                    </div>
                    <div>
                      <label htmlFor="su-password" className="label-base">{t("auth.password")}</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
                        <input
                          id="su-password" type={showPw ? "text" : "password"} autoComplete="new-password"
                          value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => touch("password")}
                          className="input-base pl-10 pr-12 min-h-[48px]" placeholder={t("auth.passwordPlaceholder")}
                          aria-invalid={!!show("password")} aria-describedby="password-rules" data-testid="signup-password"
                        />
                        <button type="button" onClick={() => setShowPw(!showPw)}
                                aria-label={t("auth.password")}
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center text-smoke hover:text-worker"
                                data-testid="signup-pw-toggle">
                          {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <div id="password-rules"><PasswordRules value={password} /></div>
                    </div>
                  </>
                )}

                {isProvider && (
                  <div>
                    <label htmlFor="su-phone" className="label-base">{t("signupX.phone")}</label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
                      <input
                        id="su-phone" type="tel" inputMode="tel" autoComplete="tel-national" value={phone}
                        onChange={(e) => setPhone(e.target.value)} onBlur={() => touch("phone")}
                        className="input-base pl-10 min-h-[48px]" placeholder="(555) 000-0000"
                        aria-invalid={!!show("phone")} data-testid="signup-phone"
                      />
                    </div>
                    {show("phone")
                      ? <FieldError testid="signup-phone-error">{t("signupX.phoneInvalid")}</FieldError>
                      : <p className="text-xs text-smoke mt-1">{t("signupX.phoneHint")}</p>}
                  </div>
                )}

                <div>
                  <label htmlFor="su-zip" className="label-base">{t("signupX.zip")}</label>
                  <div className="relative">
                    <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
                    <input
                      id="su-zip" inputMode="numeric" autoComplete="postal-code" maxLength={5} value={zip}
                      onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                      onBlur={() => touch("zip")}
                      className="input-base pl-10 min-h-[48px]" placeholder="33101"
                      aria-invalid={!!show("zip")} data-testid="signup-zip"
                    />
                  </div>
                  {show("zip")
                    ? <FieldError testid="signup-zip-error">{t("signupX.zipInvalid")}</FieldError>
                    : <p className="text-xs text-smoke mt-1">{t("signupX.zipHint")}</p>}
                </div>

                {usesPassword && (
                  <label className="flex items-start gap-3 text-sm text-worker cursor-pointer min-h-[44px]" data-testid="signup-legal-label">
                    <input
                      type="checkbox" checked={acceptedLegal}
                      onChange={(e) => setAcceptedLegal(e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-hive-deep" data-testid="signup-accept-legal"
                    />
                    <span>
                      <Trans
                        i18nKey="auth.acceptLegal"
                        components={{
                          terms: <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-worker underline hover:text-hive-ink" />,
                          privacy: <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-worker underline hover:text-hive-ink" />,
                        }}
                      />
                    </span>
                  </label>
                )}

                {/* The screen's one primary. `loading` keeps the label and adds a
                    spinner, so the button never changes width mid-press. */}
                <Button type="submit" fullWidth loading={loading} data-testid="signup-submit">
                  {isProvider ? t("signupX.continue") : t("auth.signupBtn")}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <div data-testid="signup-step-category">
              <Button type="button" variant="ghost" icon="arrow-left" onClick={() => setStep(1)}
                      className="mb-3 -ml-2" data-testid="signup-back">
                {t("signupX.back")}
              </Button>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-worker text-balance">{t("signupX.categoryTitle")}</h1>
              <p className="mt-2 mb-5 text-smoke">{t("signupX.categorySubtitle")}</p>
              <CategoryPicker value={categories} onChange={setCategories} max={1} testid="signup-category" />
              <div className="sticky bottom-0 -mx-4 px-4 py-4 bg-cream mt-4 border-t border-sand">
                <Button
                  type="button" fullWidth onClick={create} loading={loading}
                  disabled={categories.length === 0} data-testid="signup-submit-provider"
                >
                  {converting ? t("signupX.convertCta") : t("auth.signupBtn")}
                </Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="mt-6 space-y-2 text-sm text-center text-smoke">
              {!finishing && !converting && (
                <p>
                  {t("auth.haveAccount")}{" "}
                  <Link to={withReturnTo("/login", returnTo)} className="font-semibold text-worker hover:text-hive-ink underline" data-testid="signup-login-link">
                    {t("auth.loginBtn")}
                  </Link>
                </p>
              )}
              {!converting && <p>
                {isProvider ? t("signupX.crossToClient") : t("signupX.crossToProvider")}{" "}
                <Link to={withReturnTo(otherSide, returnTo)} className="font-semibold text-worker hover:text-hive-ink underline" data-testid="signup-cross-link">
                  {t("signupX.crossLink")}
                </Link>
              </p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function dashboardOf(user) {
  if (user?.profile?.is_admin) return "/admin";
  return user?.profile?.role === "provider" ? "/provider/dashboard" : "/client/dashboard";
}

/** Name and address behind a Google access token, to show who is signing up. */
async function googleProfile(token) {
  try {
    const r = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return r.ok ? await r.json() : {};
  } catch {
    return {};
  }
}
